import { useEffect, useRef } from "react";
import {
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { Loader2, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ShareRecordsView } from "@/components/share/share-records-view/share-records-view";
import { useShareMetadata } from "@/components/share/share-records-view/use-share-metadata";
import { useJoinShareLink } from "@/components/share/share-records-view/use-join-share-link";

export const Route = createFileRoute("/projects/share/$token")({
  component: SharePage,
});

function FullScreenSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
    </div>
  );
}

function ShareErrorState() {
  const navigate = useNavigate();
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-4 p-8 text-center">
      <Unlink className="h-10 w-10 text-muted-foreground" />
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">Invalid or Expired Link</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          This share link is no longer valid. It may have expired or been
          revoked by the project owner.
        </p>
      </div>
      <Button onClick={() => navigate({ to: "/" })}>Go to my projects</Button>
    </main>
  );
}

/**
 * Public share page. Replicates the old server-side routing client-side:
 * invalid token → error, authenticated member → redirect into the project,
 * authenticated non-member → auto-join then redirect, anonymous (or join
 * failed) → read-only share view. See [[share-page.spec]].
 */
function SharePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const metadataQuery = useShareMetadata(token);
  const join = useJoinShareLink(token);
  const joinAttempted = useRef(false);

  const metadata = metadataQuery.data;

  useEffect(() => {
    if (!metadata || !metadata.is_authenticated) return;

    if (metadata.is_member) {
      navigate({
        to: "/projects/$projectId/records",
        params: { projectId: metadata.id },
        replace: true,
      });
      return;
    }

    // Authenticated non-member → auto-join once, then redirect into the joined
    // project. On failure we fall through to the read-only share view (the
    // render branch below flips once `join.isError` is set). The project id
    // comes from metadata: the join's "already a member" reply carries none.
    if (!joinAttempted.current) {
      joinAttempted.current = true;
      join.mutate(undefined, {
        onSuccess: () => {
          navigate({
            to: "/projects/$projectId/records",
            params: { projectId: metadata.id },
            replace: true,
          });
        },
      });
    }
  }, [metadata, navigate, join]);

  // `isPending`, not `isLoading`: the metadata query is disabled while the
  // WorkOS session resolves, and a disabled query is pending but not loading.
  if (metadataQuery.isPending) return <FullScreenSpinner />;
  if (metadataQuery.isError || !metadata) return <ShareErrorState />;

  // Member redirect, or non-member auto-join still in flight: keep the spinner
  // up while we navigate away. Once a join errors, fall through to the view.
  if (metadata.is_authenticated && metadata.is_member) {
    return <FullScreenSpinner />;
  }
  if (
    metadata.is_authenticated &&
    !metadata.is_member &&
    !join.isError
  ) {
    return <FullScreenSpinner />;
  }

  return <ShareRecordsView token={token} metadata={metadata} />;
}
